/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect} from "react";
import DatePicker, {registerLocale, setDefaultLocale} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Dates from "utils/Dates";
import {BOOKING_MAXIMUM_DATE, DEFAULT_RESERVATION_DAYS_FROM_NOW, FERIENPASS_MAXIMUM_DATE, LOCALE} from "helpers/config";
import {useTypedSelector} from "helpers/reducers";
import {UseFormSetValue} from "react-hook-form/dist/types";
import {BookingFilter} from "types/app";
import {declareLocaleByConfig} from "utils/localeUtils";
import {packageCategoryExcludeDates, packageCategoryMinimalDate, useHallHolidays} from "utils/packageUtils";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {PackageCategory} from "types/enums/PackageCategory";
import "assets/components/react-datepicker-inline-custom.css";
import BookingCardTitle from "pages/Front/Booking/components/BookingCardTitle";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import {usePackageCategory} from "utils/hooks/usePackageCategory";
import DatePickerCustomHeader
    from "pages/Front/Booking/components/BookingFilters/BookingDateFilterInline/DatePickerCustomHeader";

interface BookingDateFilterInlineProps {
    setValue: UseFormSetValue<BookingFilter>;
    register: any;
    showTitle: boolean;
    onChange: (date: string) => void;
    packageCategory: PackageCategory;
    disabled?: boolean;
    todayTimeslotsBookable: boolean;
}

const BookingDateFilterInline = (props: BookingDateFilterInlineProps) => {
    dayjs.extend(isSameOrBefore);
    dayjs.extend(isSameOrAfter);
    const bookingFilter = useTypedSelector(state => state.bookingState.bookingProcess.filter);
    const {getCategoryBySlug, packageCategoryCardHandle} = usePackageCategory();
    const {getExcludedDays} = useHallOpeningHours();
    const [selected, setSelected] = React.useState<any>(null);
    const minimalDatePreset = new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);
    const startDate = useTypedSelector(state => state.bookingState.bookingProcess.filter.date);
    const selectedPackageCategory: PackageCategory = props.packageCategory;

    const packageEntity = getCategoryBySlug(selectedPackageCategory);

    const minimalDate = selectedPackageCategory === null ? new Date(minimalDatePreset) : packageCategoryMinimalDate(packageEntity);
    // const maximumDate =         selectedPackageCategory === null ? addMonths(new Date(), 7) : packageCategoryMaximumDate(packageEntity);
    let maximumDate = BOOKING_MAXIMUM_DATE;

    if (
        selectedPackageCategory == PackageCategory.FerienPass ||
        selectedPackageCategory == PackageCategory.FerienPassB ||
        selectedPackageCategory == PackageCategory.ThreeTwo
    ) {
        maximumDate = FERIENPASS_MAXIMUM_DATE;
    }
    const excludeDaysFromPackageCategory = selectedPackageCategory ? packageCategoryExcludeDates(selectedPackageCategory, minimalDate.getMonth()) : [];

    // console.log("excludeDaysFromPackageCategory", excludeDaysFromPackageCategory, selectedPackageCategory);
    const excludedDays = getExcludedDays();
    const isTodayExcluded = !props.todayTimeslotsBookable ? new Date() : null;
    if (isTodayExcluded) {
        excludeDaysFromPackageCategory.push(isTodayExcluded);
    }
    excludeDaysFromPackageCategory.push(...excludedDays);

    const {filteredExcludedDays, getHallHolidays} = useHallHolidays(selectedPackageCategory);
    const excludeDays = excludeDaysFromPackageCategory;
    let filteredDays = filteredExcludedDays(excludeDays);
    // console.log("excludeDays", excludeDays, "filteredDatys", filteredDays);

    if (selectedPackageCategory === PackageCategory.School) {
        const specificDatesToRemove = [
            dayjs("2025-06-16").toDate(),
            dayjs("2025-06-17").toDate(),
            dayjs("2025-06-23").toDate(),
            dayjs("2025-06-24").toDate(),
            dayjs("2025-06-30").toDate(),
            dayjs("2025-07-01").toDate(),
        ];
        filteredDays = filteredDays.filter(date => !specificDatesToRemove.some(specificDate => dayjs(date).isSame(specificDate, "day")));
    }

    if (
        selectedPackageCategory === PackageCategory.FerienPass ||
        selectedPackageCategory === PackageCategory.FerienPassB ||
        selectedPackageCategory === PackageCategory.ThreeTwo
    ) {
        const specificDatesToRemove = [dayjs("2026-05-16").toDate(), dayjs("2026-05-17").toDate()];

        filteredDays.push(...specificDatesToRemove);
        // filteredDays = filteredDays.filter(
        //     date => !specificDatesToRemove.some(specificDate => dayjs(date).isSame(specificDate, "day")),
        // );
    }

    // console.log("filteredDays", filteredDays, selectedPackageCategory);
    const getClosestAvailableDate = (currentDate: Date): Date => {
        let dateToCheck = dayjs(currentDate);
        while (excludeDays.some(date => dayjs(date).isSame(dateToCheck, "day"))) {
            dateToCheck = dateToCheck.add(1, "day");
        }
        return dateToCheck.toDate();
    };

    const isStartDateSameAsFilterDate = (date1: Date, date2: Date): boolean => {
        return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
    };

    const startDateFromFilter = () => {
        const startDateFormatted = new Date(Dates.ymd(minimalDate));
        const closestAvailableDate = getClosestAvailableDate(startDateFormatted);
        const today = new Date();
        if (closestAvailableDate > today) {
            return closestAvailableDate;
        }
        return today;
    };

    const dateHandler = (date): void => {
        // setStartDate(date);
        props.setValue("date", Dates.ymd(date));
        props.onChange(Dates.ymd(date));
        setSelected(date);
    };

    const datePickerLocale = declareLocaleByConfig(LOCALE);
    registerLocale("locale", datePickerLocale);
    setDefaultLocale(datePickerLocale);

    useEffect(() => {
        const closestDay = startDateFromFilter();
        const filterDate = bookingFilter?.date ? new Date(Dates.ymd(bookingFilter.date)) : new Date();
        setSelected(closestDay);
        if (!isStartDateSameAsFilterDate(closestDay, filterDate)) {
            dateHandler(closestDay);
        }
    }, []);

    return (
        <div className={"BookingDateFilterInline flex flex-col relative"}>
            {props.showTitle && <BookingCardTitle title={"Desired date"} />}

            <div className={"BookingDateFilterInlineCalendarWrapper mt-3 flex-col w-full flex justify-end"}>
                <DatePicker
                    className={"BookingDateFilterInlineCalendar"}
                    dateFormat={"dd.MM.yyyy"}
                    excludeDates={filteredDays}
                    locale="locale"
                    maxDate={maximumDate}
                    minDate={minimalDate}
                    popperPlacement={"bottom-end"}
                    renderCustomHeader={props => <DatePickerCustomHeader {...props} />}
                    selected={selected}
                    inline
                    showDisabledMonthNavigation
                    onChange={dateHandler}
                />
                <input {...props.register("date")} name={"date"} type="hidden" />
            </div>
        </div>
    );
};

export default BookingDateFilterInline;
